// Sparse-data guard (2026-10-08): the frontend twin of
// src/wcc_viewer/sample_stats.py, so every page says "not enough data" the
// same way. A statistic from fewer data points than its minimum is not
// shown as a number (no % of a tiny denominator, no median of two values,
// no "100% under budget"); it is replaced by a count + short list, the few
// values themselves, or "not enough data yet (n of N needed)".
// Minimums: config/stat_minimums.yaml (backend payloads carry `min_n`);
// reports/sparse_data_stats_2026-10-08.md lists every stat.
import { el } from "./legend";

/** The fragment the backend sends beside a sparse-able statistic (sample_stats.gate). */
export interface Sample {
  sufficient?: boolean;
  n?: number | null;
  min_n?: number | null;
  note?: string | null;
  summary?: string | null;
  values?: number[] | null;
}

export const DEFAULT_MIN_N = 5;

/** Minimums for shares the strategy dashboard computes from counts its
 * sections already carry. Those section payloads are frozen byte-for-byte
 * (P3 parity tests, prebuilt serving bundles), so the gate is applied here.
 * Mirror of config/stat_minimums.yaml `dashboard.*`. */
export const DASHBOARD_MIN = { pipes: 20, catchments: 5, packages: 3 } as const;

export function sufficient(n: number | null | undefined, min: number | null | undefined = DEFAULT_MIN_N): boolean {
  return n !== null && n !== undefined && n >= (min ?? DEFAULT_MIN_N);
}

export function plural(n: number, noun: string): string {
  if (n === 1) return noun;
  if (/[^aeo]y$/.test(noun)) return `${noun.slice(0, -1)}ies`;
  return noun.endsWith("s") ? noun : `${noun}s`;
}

/** "not enough data yet (2 of 5 items needed)" */
export function notEnoughNote(n: number, min: number | null | undefined, noun = "item"): string {
  const m = min ?? DEFAULT_MIN_N;
  return `not enough data yet (${n} of ${m} ${plural(m, noun)} needed)`;
}

/** "2 projects: A (Construct), B (Define)" */
export function countList(entries: string[], noun = "item", limit = 4): string {
  if (!entries.length) return `no ${plural(0, noun)} yet`;
  const shown = entries.slice(0, limit);
  const more = entries.length - shown.length;
  return `${entries.length} ${plural(entries.length, noun)}: ${shown.join(", ")}${more > 0 ? `, and ${more} more` : ""}`;
}

function fmtValue(v: number, unit: string): string {
  if (unit.trim() === "d" && Math.abs(v) < 1) {
    const h = v * 24;
    return h < 1 ? "<1 h" : `${Math.round(h)} h`;
  }
  const r = Math.abs(v) >= 10 ? Math.round(v) : Math.round(v * 10) / 10;
  return `${r}${unit}`;
}

/** The few values themselves instead of a median: "2 items: 34 d, 51 d". */
export function valuesSummary(values: number[], unit = " d", noun = "item", limit = 6): string {
  const vals = [...values].filter((v) => v !== null && v !== undefined).sort((a, b) => a - b);
  if (!vals.length) return `no ${plural(0, noun)} yet`;
  const more = vals.length - limit;
  return `${vals.length} ${plural(vals.length, noun)}: ${vals.slice(0, limit).map((v) => fmtValue(v, unit)).join(", ")}${more > 0 ? `, and ${more} more` : ""}`;
}

/** The standard "insufficient" text for a Sample: its summary (or the
 * values) and the not-enough note. */
export function insufficientText(s: Sample, unit = " d", noun = "item"): string {
  const n = s.n ?? 0;
  const head = s.summary ?? (s.values?.length ? valuesSummary(s.values, unit, noun) : null);
  const note = s.note ?? notEnoughNote(n, s.min_n, noun);
  return head ? `${head} (${note})` : note;
}

/** The standard "insufficient" rendering: the replacement summary (if any)
 * and a dim "not enough data yet (n of N needed)" line. */
export function insufficientEl(s: Sample, unit = " d", noun = "item"): HTMLElement {
  const box = el("span", { className: "sparse" });
  const n = s.n ?? 0;
  const head = s.summary ?? (s.values?.length ? valuesSummary(s.values, unit, noun) : null);
  if (head) box.append(el("span", { className: "sparse-summary" }, head), document.createTextNode(" "));
  box.append(el("span", { className: "sparse-note" }, s.note ?? notEnoughNote(n, s.min_n, noun)));
  return box;
}
